import React, { useState, useEffect, useMemo } from "react";
import { T } from "../lib/theme";
import { fetchSafetyRatings, fetchComplaints, splitComponents } from "../lib/safetyAndComplaints";
import { ModeBadge, Spin } from "./Atoms";

function Stars({n}){
  if(!n||n==="Not Rated"||n==="0")return <span style={{fontSize:13,color:T.textSm}}>Not Rated</span>;
  const count=parseInt(n)||0;
  return(
    <div style={{display:"flex",gap:2,alignItems:"center"}}>
      {[1,2,3,4,5].map(i=><span key={i} style={{fontSize:14,color:i<=count?"#f59e0b":"#e2e8f0"}}>★</span>)}
      <span className="mono" style={{fontSize:12,fontWeight:700,color:T.textMd,marginLeft:4}}>{count}/5</span>
    </div>
  );
}

export function SafetyProfile({vehicle}){
  const[ratings,setRatings]=useState(null);
  const[ratingsError,setRatingsError]=useState(false);
  const[rLoad,setRLoad]=useState(false);
  const[ratingsOpen,setRatingsOpen]=useState(false);
  const[complaints,setComplaints]=useState(null);
  const[complaintsSource,setComplaintsSource]=useState(null);
  const[cLoad,setCLoad]=useState(false);
  const[cOpen,setCOpen]=useState(false);
  const[cErr,setCErr]=useState("");
  useEffect(()=>{
    if(!vehicle.make||!vehicle.model||!vehicle.year)return;
    setRLoad(true);setRatingsError(false);
    fetchSafetyRatings(vehicle.year,vehicle.make,vehicle.model)
      .then(d=>{setRatings(d);setRLoad(false);})
      .catch(()=>{setRatingsError(true);setRLoad(false);});
  },[vehicle.make,vehicle.model,vehicle.year]);
  const loadC=async()=>{
    if(complaints!==null){setCOpen(o=>!o);return;}
    setCLoad(true);setCErr("");
    try{const{items,source}=await fetchComplaints(vehicle.make,vehicle.model,vehicle.year);setComplaints(items);setComplaintsSource(source);setCOpen(true);}
    catch(e){setCErr(e.message||"Failed to load.");}
    setCLoad(false);
  };
  // Pure client-side reshape of the complaints already fetched/held in
  // state above — no new network call. Counts each complaint once per
  // individual category it lists (a complaint citing 3 components
  // counts toward all 3), not once per complaint row.
  const complaintTrend=useMemo(()=>{
    if(!complaints||complaints.length===0)return[];
    const counts={};
    complaints.forEach(c=>{
      splitComponents(c.component).forEach(cat=>{counts[cat]=(counts[cat]||0)+1;});
    });
    return Object.entries(counts).sort((a,b)=>b[1]-a[1]).slice(0,3);
  },[complaints]);
  return(
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      <div style={{borderRadius:12,border:"1.5px solid "+T.border,background:T.card,overflow:"hidden"}}>
        <button onClick={()=>ratings&&setRatingsOpen(v=>!v)} style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"12px 16px",borderBottom:ratingsOpen&&ratings?"1px solid "+T.border:"none",background:"none",border:"none",textAlign:"left",cursor:ratings?"pointer":"default"}}>
          <span style={{fontSize:18}}>🛡️</span>
          <div style={{flex:1}}>
            <div style={{display:"flex",alignItems:"center"}}><div style={{fontSize:13,fontWeight:700,color:T.text}}>NHTSA Safety Ratings</div>{ratings&&<ModeBadge mode={ratings.source}/>}</div>
            <div style={{fontSize:12,color:T.textSm,marginTop:2}}>{rLoad?"Loading...":ratingsError?"Ratings temporarily unavailable":ratings?"Overall: "+ratings.overall+"/5 stars":"No rating on file"}</div>
          </div>
          {rLoad&&<Spin/>}
          {!rLoad&&ratings&&<span style={{color:T.textSm,fontSize:16,transform:ratingsOpen?"rotate(180deg)":"none",transition:"transform .25s"}}>v</span>}
        </button>
        {ratingsOpen&&ratings&&(
          <div style={{padding:"14px 16px"}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:ratings.rolloverRisk?10:0}}>
              {[["Overall",ratings.overall],["Frontal",ratings.frontal],["Side",ratings.side],["Rollover",ratings.rollover]].map(([l,v])=>(
                <div key={l}><div style={{fontSize:11,color:T.textSm,fontWeight:600,textTransform:"uppercase",letterSpacing:".05em",marginBottom:4}}>{l}</div><Stars n={v}/></div>
              ))}
            </div>
            {ratings.rolloverRisk&&<div style={{padding:"8px 12px",background:T.slateL,borderRadius:8,fontSize:12,color:T.textMd}}>Rollover probability: <strong style={{color:T.amber}}>{ratings.rolloverRisk}</strong></div>}
          </div>
        )}
      </div>
      <div style={{borderRadius:12,border:"1.5px solid "+T.border,background:T.card,overflow:"hidden"}}>
        <button onClick={loadC} style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"12px 16px",background:"none",border:"none",textAlign:"left",cursor:"pointer"}}>
          <span style={{fontSize:18}}>📋</span>
          <div style={{flex:1}}>
            <div style={{display:"flex",alignItems:"center"}}><div style={{fontSize:13,fontWeight:700,color:T.text}}>Owner Complaints</div>{complaintsSource&&<ModeBadge mode={complaintsSource}/>}</div>
            <div style={{fontSize:12,color:T.textSm}}>{complaints===null?"NHTSA complaints — tap to load":complaints.length+" on record"}</div>
          </div>
          {cLoad?<Spin/>:<span style={{color:T.textSm,fontSize:16,transform:cOpen?"rotate(180deg)":"none",transition:"transform .25s"}}>v</span>}
        </button>
        {cErr&&<div style={{padding:"8px 16px",fontSize:12,color:T.red}}>{cErr}</div>}
        {cOpen&&complaints!==null&&(
          <div style={{borderTop:"1px solid "+T.border}}>
            {complaints.length===0
              ?<div style={{padding:"12px 16px",fontSize:13,color:T.green}}>✅ No complaints on record.</div>
              :<>
                {complaintTrend.length>0&&<div style={{padding:"10px 16px",background:T.slateL,borderBottom:"1px solid "+T.border,fontSize:12,color:T.textMd}}><strong style={{color:T.text}}>Most reported</strong> (last {complaints.length} complaint{complaints.length!==1?"s":""}): {complaintTrend.map(([cat,n])=>`${cat} (${n})`).join(", ")}</div>}
                {complaints.slice(0,6).map((c,i)=>(
                  <div key={c.id} style={{padding:"11px 16px",borderBottom:i<Math.min(complaints.length,6)-1?"1px solid "+T.border:"none"}}>
                    <div style={{display:"flex",gap:6,marginBottom:5,flexWrap:"wrap"}}>
                      <span style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:4,background:T.blueL,color:T.blue,textTransform:"uppercase"}}>{c.component||"General"}</span>
                      {(c.crash||c.fire||c.injuries>0)&&<span style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:4,background:T.redL,color:T.red}}>⚠ {c.fire?"Fire":c.injuries>0?c.injuries+" injured":"Crash"}</span>}
                    </div>
                    <div style={{fontSize:12,color:T.textMd,lineHeight:1.5}}>{c.summary}</div>
                  </div>
                ))}
                {complaints.length>6&&<div style={{padding:"9px 16px",background:T.slateL,fontSize:12,color:T.textSm}}>+{complaints.length-6} more at nhtsa.gov</div>}
              </>
            }
          </div>
        )}
      </div>
    </div>
  );
}
